import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

// ROOT CAUSE FIX: never use toISOString() — it converts to UTC and shifts the
// date by the local timezone offset (e.g. IST +5:30 shifts 2024-12-25 → 2024-12-24).
// Always use local year/month/day parts instead.
const toLocalDateStr = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isWeekend = (dateStr) => {
  // Parse as local date to avoid UTC day-shift
  const [y, m, d] = dateStr.split("-").map(Number);
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6;
};

// holidays[] can contain objects { date: Date | "YYYY-MM-DD" } or raw strings.
// Normalise all holiday dates to "YYYY-MM-DD" using local parts so timezone
// never corrupts the comparison.
const isHoliday = (dateStr, holidays) =>
  holidays?.some((h) => {
    const raw = h.date ?? h; // object shape or plain string
    return toLocalDateStr(raw) === dateStr;
  });

const getWorkingDays = (from, to, holidays = []) => {
  let count = 0;
  // Build the start/end from local date parts to stay in local time throughout
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  let current = new Date(fy, fm - 1, fd);
  const end = new Date(ty, tm - 1, td);

  while (current <= end) {
    const iso = toLocalDateStr(current);
    if (!isWeekend(iso) && !isHoliday(iso, holidays)) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
};

// ─── Default blank state ──────────────────────────────────────────────────────
const DEFAULT_LEAVE = {
  type: "Medical",
  from: "",
  to: "",
  days: 0,
  halfDay: "no",
  reason: "",
};

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
export default function AddLeaveModal({
  show,
  setShow,
  newLeave,
  setNewLeave,
  leaveTypes = [],
  onAddLeave,
  holidays = [],
  existingLeaves = [],
  discretionaryUsed = 0, // how many discretionary leaves already taken this year
}) {
  const [error, setError] = useState("");

  // ── DEBUG: log holidays on every render so you can verify in browser console
  // Remove this block once holidays are confirmed working correctly
  React.useEffect(() => {
    console.log(
      "[AddLeaveModal] holidays received:",
      holidays.length,
      holidays,
    );
  }, [holidays]);

  // ─────────────────────────────
  // CLOSE — wipe everything
  // ─────────────────────────────
  const handleClose = () => {
    setNewLeave({ ...DEFAULT_LEAVE });
    setError("");
    setShow(false);
  };

  // ─────────────────────────────
  // TYPE CHANGE — wipe all fields except type
  // ─────────────────────────────
  const handleTypeChange = (value) => {
    setError("");
    // Block if user tries to select Discretionary but already has 1 this year
    if (value === "Discretionary Leave" && discretionaryUsed >= 1) {
      setError(
        "You have already used your 1 Discretionary Leave for this year.",
      );
      return;
    }
    setNewLeave({
      ...DEFAULT_LEAVE,
      type: value,
    });
  };

  // ─────────────────────────────
  // HALF DAY LOGIC
  // ─────────────────────────────
  const handleHalfDayChange = (value) => {
    setError("");
    setNewLeave((prev) => {
      const updated = { ...prev, halfDay: value };
      if (value === "yes") {
        updated.days = 0.5;
        updated.to = updated.from || "";
      } else {
        updated.days =
          updated.from && updated.to
            ? getWorkingDays(updated.from, updated.to, holidays)
            : 0;
      }
      return updated;
    });
  };

  // ─────────────────────────────
  // CONFLICT CHECK
  // Aligns with backend: exact same from+to is a duplicate.
  // We also check overlapping ranges as a UX warning.
  // ─────────────────────────────
  const hasConflict = (from, to) => {
    const f = new Date(from);
    const t = new Date(to);
    return existingLeaves.some((l) => {
      if (!l.from || !l.to) return false;
      const lf = new Date(l.from);
      const lt = new Date(l.to);
      return f <= lt && t >= lf;
    });
  };

  // ─────────────────────────────
  // DATE HANDLER
  // ─────────────────────────────
  const handleDateChange = (key, value) => {
    setError("");

    setNewLeave((prev) => {
      let updated = { ...prev, [key]: value };

      // ❌ Block weekend
      if (isWeekend(value)) {
        setError("Weekend cannot be selected");
        return prev;
      }

      // ❌ Block holiday
      if (isHoliday(value, holidays)) {
        setError("This date is a holiday for your location");
        return prev;
      }

      // 🤱 Maternity — auto-set to 181 days (180 nights)
      if (updated.type === "Maternity" && key === "from") {
        const fromDate = new Date(value);
        const toDate = new Date(fromDate);
        toDate.setDate(toDate.getDate() + 179);

        updated.from = value;
        updated.to = toLocalDateStr(toDate);
        updated.days = 180;
        return updated;
      }

      // 🎯 Discretionary — single day, 0 days count
      if (updated.type === "Discretionary Leave") {
        updated.from = value;
        updated.to = value;
        updated.days = 0;
        return updated;
      }

      // ½ Half day — lock to single day
      if (updated.halfDay === "yes") {
        updated.from = value;
        updated.to = value;
        updated.days = 0.5;
        return updated;
      }

      // 📅 Full day — calculate working days when both dates set
      if (updated.from && updated.to) {
        if (new Date(updated.from) > new Date(updated.to)) {
          if (key === "from") updated.to = value;
          else updated.from = value;
        }

        if (hasConflict(updated.from, updated.to)) {
          setError("You already have a leave in this date range");
          return prev;
        }

        updated.days = getWorkingDays(updated.from, updated.to, holidays);
      }
      if (updated.days > 1) {
  updated.halfDay = "no";
}

      return updated;
    });
  };

  // ─────────────────────────────
  // DERIVED FLAGS
  // ─────────────────────────────
  const isDiscretionary = newLeave.type === "Discretionary Leave";
  const discRemaining = Math.max(0, 1 - discretionaryUsed); // max 1 per year
  const isMaternity = newLeave.type === "Maternity";
  const isHalfDay = newLeave.halfDay === "yes";
  const disableHalfDay = newLeave.days > 1;
  const toDisabled = isHalfDay || isDiscretionary || isMaternity;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 500,
            backdropFilter: "blur(5px)",
          }}
        >
          {/* ── Modal box ── */}
          <motion.div
            initial={{ scale: 0.86, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.86, y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 480,
              maxHeight: "85vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              border: "1px solid #e2e8f0",
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            }}
          >
            {/* ── Header ── */}
            <div
              style={{
                padding: "20px 28px 16px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 800,
                  color: "#111827",
                }}
              >
                Add New Leave
              </h3>
              <button
                onClick={handleClose}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 20,
                  cursor: "pointer",
                  color: "#94a3b8",
                  lineHeight: 1,
                  padding: "0 4px",
                }}
              >
                ✕
              </button>
            </div>

            {/* ── Body ── */}
            <div style={{ padding: "20px 28px 28px", overflowY: "auto" }}>
              {/* Force dd/mm/yyyy on all date inputs inside this modal */}
              <style>{`
                input[type="date"]::-webkit-datetime-edit-fields-wrapper { direction: ltr; }
                input[type="date"]::-webkit-datetime-edit-month-field { order: 2; }
                input[type="date"]::-webkit-datetime-edit-day-field   { order: 1; }
                input[type="date"]::-webkit-datetime-edit-year-field  { order: 3; }
              `}</style>

              {/* Error */}
              {error && (
                <div
                  style={{
                    background: "#fee2e2",
                    color: "#b91c1c",
                    padding: "10px 14px",
                    borderRadius: 8,
                    fontSize: 13,
                    marginBottom: 14,
                    border: "1px solid #fecaca",
                  }}
                >
                  ⚠️ {error}
                </div>
              )}

              {/* Leave Type */}
              <label style={labelStyle}>Leave Type</label>
              <select
                value={newLeave.type}
                onChange={(e) => handleTypeChange(e.target.value)}
                style={inputStyle}
              >
                {leaveTypes.map((t) => (
  <option key={t.id} value={t.name}>
    {t.name}
  </option>
))}
              </select>

              {/* Discretionary info banner */}
              {isDiscretionary && (
                <div
                  style={{
                    background: "#f5f3ff",
                    border: "1px solid #ddd6fe",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 12,
                    color: "#6d28d9",
                    marginTop: 8,
                  }}
                >
                  🎯 Discretionary Leave does not count against your balance.
                  You are allowed <strong>1 per year</strong> — you have{" "}
                  <strong>{discRemaining} remaining</strong> this year.
                </div>
              )}

              {/* Maternity info banner */}
              {isMaternity && (
                <div
                  style={{
                    background: "#fdf2f8",
                    border: "1px solid #f0abfc",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: 12,
                    color: "#86198f",
                    marginTop: 8,
                  }}
                >
                  🤱 Maternity leave is automatically set to{" "}
                  <strong>181 days</strong> from the start date. Monthly accrual
                  is frozen during this period and days are not deducted from
                  your balance.
                </div>
              )}

              {/* From */}
              <label style={labelStyle}>From</label>
              <input
                type="date"
                lang="en-GB"
                value={newLeave.from}
                onChange={(e) => handleDateChange("from", e.target.value)}
                style={{ ...inputStyle, colorScheme: "light" }}
              />

              {/* To */}
              <label style={labelStyle}>To</label>
              <input
                type="date"
                lang="en-GB"
                value={newLeave.to}
                disabled={toDisabled}
                min={newLeave.from}
                onChange={(e) => handleDateChange("to", e.target.value)}
                style={{
                  ...inputStyle,
                  colorScheme: "light",
                  opacity: toDisabled ? 0.5 : 1,
                  cursor: toDisabled ? "not-allowed" : "pointer",
                }}
              />

              {/* Half Day — hide for Maternity and Discretionary */}
              {!isMaternity && !isDiscretionary && (
                <>
                  <label style={labelStyle}>Half Day</label>
                  <div style={{ display: "flex", gap: 20, marginBottom: 4 }}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        checked={isHalfDay}
                        disabled={disableHalfDay}
                        onChange={() => handleHalfDayChange("yes")}
                        style={{ accentColor: "#7c3aed" }}
                      />
                      Yes
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        checked={!isHalfDay}
                        disabled={disableHalfDay}
                        onChange={() => handleHalfDayChange("no")}
                        style={{ accentColor: "#7c3aed" }}
                      />
                      No
                    </label>
                  </div>
                </>
              )}

              {/* No. of Days */}
              <label style={labelStyle}>No. of Days</label>
              <input
                type="number"
                value={newLeave.days}
                disabled
                style={{ ...inputStyle, opacity: 0.6, cursor: "not-allowed" }}
              />

              {/* Reason */}
              <label style={labelStyle}>Reason</label>
              <textarea
                value={newLeave.reason}
                onChange={(e) =>
                  setNewLeave((p) => ({ ...p, reason: e.target.value }))
                }
                rows={3}
                placeholder="Enter reason for leave..."
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  fontFamily: "inherit",
                  fontSize: 14,
                  lineHeight: 1.5,
                }}
              />

              {/* Buttons */}
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                <button onClick={handleClose} style={btnCancelStyle}>
                  Cancel
                </button>
                <button
                  onClick={onAddLeave}
                  disabled={!newLeave.from || !newLeave.to}
                  style={{
                    ...btnSaveStyle,
                    opacity: !newLeave.from || !newLeave.to ? 0.5 : 1,
                    cursor:
                      !newLeave.from || !newLeave.to
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  Add Leave
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const labelStyle = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: "#94a3b8",
  marginTop: 14,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1.5px solid #e2e8f0",
  background: "#f8fafc",
  outline: "none",
  fontSize: 14,
  color: "#111827",
  fontFamily: "inherit",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const btnCancelStyle = {
  flex: 1,
  padding: "11px 0",
  borderRadius: 10,
  border: "1.5px solid #e2e8f0",
  background: "#fff",
  fontWeight: 600,
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "inherit",
  color: "#374151",
};

const btnSaveStyle = {
  flex: 1,
  padding: "11px 0",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(135deg, #7c3aed, #a855f7)",
  color: "#fff",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
  fontFamily: "inherit",
};
